import React, { useState, useEffect } from "react";
import { Wallet, Shield, User, DollarSign } from "lucide-react";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import adminApi from "../../../api/adminApi";
import '../../../assets/css/admin/settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);

  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: ""
  });

  const [walletBalance, setWalletBalance] = useState(0);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchProfile();
    fetchWallet();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await adminApi.get("/profile");
      setProfileData({
        name: res.data.name,
        email: res.data.email,
        phone: res.data.phone || ""
      });
    } catch (err) {
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWallet = async () => {
    try {
      const res = await adminApi.get("/wallet");
      setWalletBalance(Number(res.data.wallet_balance));
      setPendingBalance(Number(res.data.pending_balance));
      setTotalEarnings(Number(res.data.total_earnings));
      setTransactions(res.data.transactions.data || []);
    } catch (err) {
      console.error('Wallet fetch error:', err);
    }
  };

  const handleProfileSave = async () => {
    try {
      await adminApi.put("/profile", profileData);
      Swal.fire({ title: 'Profile Updated!', text: 'Your profile information has been saved successfully.', icon: 'success', confirmButtonColor: '#ff7a00', timer: 2000 });
    } catch (err) {
      const message = err.response?.data?.message || 'Something went wrong';
      Swal.fire({ title: 'Update Failed', text: message, icon: 'error', confirmButtonColor: '#ff7a00' });
    }
  };

  const handlePasswordUpdate = () => {
    Swal.fire({
      title: 'Change Password',
      html: `
        <input id="currentPassword" class="swal2-input" placeholder="Current password" type="password">
        <input id="newPassword" class="swal2-input" placeholder="New password" type="password">
        <input id="confirmPassword" class="swal2-input" placeholder="Confirm new password" type="password">
      `,
      showCancelButton: true,
      confirmButtonText: 'Update Password',
      confirmButtonColor: '#ff7a00',
      cancelButtonColor: '#64748b',
      preConfirm: () => {
        const currentPassword = document.getElementById('currentPassword').value;
        const newPassword = document.getElementById('newPassword').value;
        const confirmPassword = document.getElementById('confirmPassword').value;
        if (!currentPassword || !newPassword || !confirmPassword) {
          Swal.showValidationMessage('Please fill all fields');
          return false;
        }
        if (newPassword !== confirmPassword) {
          Swal.showValidationMessage('New passwords do not match');
          return false;
        }
        return { currentPassword, newPassword, confirmPassword };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await adminApi.put("/profile/password", {
            current_password: result.value.currentPassword,
            new_password: result.value.newPassword,
            new_password_confirmation: result.value.confirmPassword
          });
          Swal.fire({ title: 'Password Updated!', text: 'Your password has been changed successfully.', icon: 'success', confirmButtonColor: '#ff7a00', timer: 2000 });
        } catch (err) {
          const message = err.response?.data?.message || 'Password update failed';
          Swal.fire({ title: 'Failed', text: message, icon: 'error', confirmButtonColor: '#ff7a00' });
        }
      }
    });
  };

  const handleWithdrawal = () => {
    Swal.fire({
      title: 'Withdraw Funds',
      html: `
        <div style="text-align: left; margin-bottom: 15px;">
          <p style="margin: 0 0 8px 0; color: #64748b; font-size: 13px;">Available Balance</p>
          <p style="margin: 0; font-size: 24px; font-weight: 700; color: #16a34a;">Rs. ${walletBalance.toLocaleString()}</p>
        </div>
        <input id="amount" class="swal2-input" placeholder="Enter amount" type="number" style="margin-top: 10px;">
        <input id="accountNumber" class="swal2-input" placeholder="Account number" type="text">
        <select id="method" class="swal2-input">
          <option value="">Select withdrawal method</option>
          <option value="bank">Bank Transfer (2-3 business days)</option>
          <option value="easypaisa">Easypaisa (Instant)</option>
          <option value="jazzcash">JazzCash (Instant)</option>
        </select>
      `,
      showCancelButton: true,
      confirmButtonText: 'Confirm Withdrawal',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ff7a00',
      cancelButtonColor: '#64748b',
      width: '500px',
      preConfirm: () => {
        const amount = document.getElementById('amount').value;
        const accountNumber = document.getElementById('accountNumber').value;
        const method = document.getElementById('method').value;
        if (!amount || !accountNumber || !method) { Swal.showValidationMessage('Please fill all fields'); return false; }
        if (amount < 500) { Swal.showValidationMessage('Minimum withdrawal amount is Rs. 500'); return false; }
        if (amount > walletBalance) { Swal.showValidationMessage('Insufficient balance'); return false; }
        return { amount, accountNumber, method };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await adminApi.post("/wallet/withdraw", {
            amount: result.value.amount,
            account_number: result.value.accountNumber,
            method: result.value.method
          });
          const methodName = result.value.method === 'bank' ? 'Bank Transfer' : result.value.method === 'easypaisa' ? 'Easypaisa' : 'JazzCash';
          await fetchWallet();
          Swal.fire({
            title: 'Withdrawal Request Submitted!',
            html: `<div style="text-align: left; padding: 20px;">
              <p style="margin: 10px 0;"><strong>Amount:</strong> Rs. ${Number(result.value.amount).toLocaleString()}</p>
              <p style="margin: 10px 0;"><strong>Method:</strong> ${methodName}</p>
              <p style="margin: 10px 0;"><strong>Account:</strong> ${result.value.accountNumber}</p>
              <p style="margin: 10px 0; color: #64748b; font-size: 14px;">Your withdrawal will be processed within 24 hours.</p>
            </div>`,
            icon: 'success', confirmButtonColor: '#ff7a00', confirmButtonText: 'Done'
          });
        } catch (err) {
          const message = err.response?.data?.message || 'Withdrawal failed';
          Swal.fire({ title: 'Failed', text: message, icon: 'error', confirmButtonColor: '#ff7a00' });
        }
      }
    });
  };

  if (loading) {
    return <div className="settings-wrapper"><p>Loading...</p></div>;
  }

  return (
    <div className="settings-wrapper">
      <div className="settings-container">
        <div className="settings-header">
          <h1 className="settings-title">Settings</h1>
          <p className="settings-subtitle">Manage your account settings and preferences</p>
        </div>

        <div className="settings-tabs">
          <button className={`settings-tab ${activeTab === "profile" ? "settings-tab-active" : ""}`} onClick={() => setActiveTab("profile")}>
            <User size={18} /> Profile
          </button>
          <button className={`settings-tab ${activeTab === "wallet" ? "settings-tab-active" : ""}`} onClick={() => setActiveTab("wallet")}>
            <Wallet size={18} /> Wallet
          </button>
          <button className={`settings-tab ${activeTab === "security" ? "settings-tab-active" : ""}`} onClick={() => setActiveTab("security")}>
            <Shield size={18} /> Security
          </button>
        </div>

        <div className="settings-content">

          {activeTab === "profile" && (
            <div className="settings-section">
              <h2 className="settings-section-title">Profile Information</h2>
              <div className="settings-fields">
                <div className="settings-field">
                  <label>Full Name</label>
                  <input type="text" placeholder="Enter full name" value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} />
                </div>
                <div className="settings-field">
                  <label>Email</label>
                  <input type="email" placeholder="Enter email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} />
                </div>
                <div className="settings-field">
                  <label>Phone</label>
                  <input type="text" placeholder="Enter phone number" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} />
                </div>
                <button className="settings-save-btn" onClick={handleProfileSave}>Save Changes</button>
              </div>
            </div>
          )}

          {activeTab === "wallet" && (
            <div className="settings-section">
              <h2 className="settings-section-title">Wallet Management</h2>
              <div className="wallet-stats-grid">
                <div className="wallet-stat-card wallet-stat-primary">
                  <div className="wallet-stat-header"><Wallet size={24} /><span className="wallet-stat-badge">Available</span></div>
                  <div className="wallet-stat-amount">Rs. {walletBalance.toLocaleString()}</div>
                  <div className="wallet-stat-label">Current Balance</div>
                </div>
                <div className="wallet-stat-card wallet-stat-warning">
                  <div className="wallet-stat-header"><span className="wallet-stat-badge">Pending</span></div>
                  <div className="wallet-stat-amount">Rs. {pendingBalance.toLocaleString()}</div>
                  <div className="wallet-stat-label">Processing</div>
                </div>
                <div className="wallet-stat-card wallet-stat-success">
                  <div className="wallet-stat-header"><DollarSign size={24} /><span className="wallet-stat-badge">Total</span></div>
                  <div className="wallet-stat-amount">Rs. {totalEarnings.toLocaleString()}</div>
                  <div className="wallet-stat-label">Total Earnings</div>
                </div>
              </div>

              <div className="wallet-actions">
                <button className="wallet-action-btn wallet-action-primary" onClick={handleWithdrawal}>
                  <DollarSign size={20} /> Withdraw Funds
                </button>
              </div>

              <div className="wallet-transactions">
                <div className="wallet-transactions-header">
                  <h3 className="wallet-transactions-title">Transaction History</h3>
                  <button className="wallet-download-btn">Download Statement</button>
                </div>
                <div className="wallet-transactions-list">
                  {transactions.length === 0 && <p style={{ color: '#64748b', fontSize: '14px' }}>No transactions yet.</p>}
                  {transactions.map(transaction => (
                    <div key={transaction.id} className="wallet-transaction-item">
                      <div className="wallet-transaction-left">
                        <div className={`wallet-transaction-icon ${transaction.type.toLowerCase()}`}>
                          {transaction.type === "Credit" ? "↓" : "↑"}
                        </div>
                        <div className="wallet-transaction-info">
                          <div className="wallet-transaction-desc">{transaction.description}</div>
                          <div className="wallet-transaction-meta">
                            <span className="wallet-transaction-date">{new Date(transaction.created_at).toLocaleDateString()}</span>
                            <span className="wallet-transaction-dot">•</span>
                            <span className="wallet-transaction-time">{new Date(transaction.created_at).toLocaleTimeString()}</span>
                            <span className={`wallet-transaction-status ${transaction.status.toLowerCase()}`}>{transaction.status}</span>
                          </div>
                        </div>
                      </div>
                      <div className={`wallet-transaction-amount ${transaction.type.toLowerCase()}`}>
                        {transaction.type === "Credit" ? "+" : "-"} Rs. {Number(transaction.amount).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="settings-section">
              <h2 className="settings-section-title">Change Password</h2>
              <div className="settings-fields">
                <p style={{ color: '#64748b', fontSize: '14px' }}>Click below to open the secure password change dialog.</p>
                <button className="settings-save-btn" onClick={handlePasswordUpdate}>Update Password</button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Settings;